// Vincent B683 (Eric 2026-02-XX) — Panneau Transcript extrait de
// VideoDetailPage.js (~340 lignes). Wrapper qui contient :
//   - TranscriptSearchBar (recherche filtrée)
//   - Sélecteur de source (Whisper / Fireflies / Fusionnée)
//   - Bouton « Rapport de fusion » (si merged existe)
//   - Boutons persistants « Remplacer Fireflies » + « Générer/Refaire la fusion »
//   - Speaker legend
//   - Sous-onglets WhisperSubTab / FirefliesSubTab / MergedSubTab
//
// Objectif : maintenir VideoDetailPage.js < 2500 lignes.
import React, { useCallback, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { GROUP_SETTINGS, GROUP_RERUN, GROUP_UTIL, ToolbarSeparator, ToolbarGroupLabel } from "../../lib/transcriptToolbarStyles"; // Vincent B966 (Eric 2026-02-14) + tâches 1307 · 1310
import { useCollapsedToolbarGroups } from "../../lib/useCollapsedToolbarGroups"; // Tâche 1312
import { useAuth } from "../../contexts/AuthContext";

import TranscriptSearchBar from "./TranscriptSearchBar";
import FirefliesImportPanel from "./FirefliesImportPanel";
import WhisperSubTab from "./WhisperSubTab";
import FirefliesSubTab from "./FirefliesSubTab";
import MergedSubTab from "./MergedSubTab";
import MergedToolbar from "./MergedToolbar";
import SpeakerLegendBox from "./SpeakerLegendBox";
import SpeakerRenameLogPanel from "./SpeakerRenameLogPanel";
// Tâche 1142 (Eric 2026-06-16) — Avertissement quand la transcription vient
// de Gemini (horodatage par mot approximatif).
import TranscriptionProviderNotice from "./TranscriptionProviderNotice";
// Vincent B939 (Eric 2026-02-14 SPEC STRICK PROD) — Modal Correction
// Bulk Apply (liste des N corrections en attente + multi-select).
import PendingCorrectionsModal from "./PendingCorrectionsModal";
import { isSameSpeakerName } from "../../lib/speakerNormalize";
import CoverageGapsModal from "./CoverageGapsModal"; // Tâche 1252
import SyncCurseursBandeau from "./SyncCurseursBandeau"; // Tâche 1444
// Tâche 1453 — le réglage des curseurs synchronisés vit dans la bibliothèque :
// la case (barre d'outils) et le bandeau d'état (sous la barre) doivent lire
// et écrire LA MÊME valeur.
import { ecrireReglageSync, lireReglageSync } from "../../lib/syncTranscriptions";

export default function TranscriptPanel(props) {
  // Tâche 1252 — « Écarts » : QUELS passages manquent, pas juste un %.
  const [gapsOpen, setGapsOpen] = useState(false);
  // Tâche 1453 — réglage « curseurs synchronisés », lu une fois au montage et
  // partagé avec le bandeau d'état. Allumé par défaut (choix de 1444).
  const [syncCurseurs, setSyncCurseurs] = useState(lireReglageSync);
  // Tâche 1312 — Blocs de boutons repliables, choix mémorisé d'un fichier au
  // suivant (même hook partagé par les trois barres).
  const { isCollapsed, toggleGroup } = useCollapsedToolbarGroups();
  // Tâche 1286 (Eric 2026-06-29) — Recalcul explicite des écarts de CETTE
  // vidéo : le rapport est servi frais, puis la fenêtre s'ouvre dessus.
  const [detectingGaps, setDetectingGaps] = useState(false);
  const detectGaps = async () => {
    setDetectingGaps(true);
    try {
      const { data } = await axios.get(
        `${API}/api/videos/${videoId}/coverage-gaps`,
        { withCredentials: true, timeout: 120000 },
      );
      toast.success(
        data.total > 0
          ? `${data.total} écart(s) détecté(s) — ${data.total_missing_sec} s manquantes`
          : "Aucun écart détecté sur cette vidéo.",
      );
      setGapsOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Détection impossible");
    } finally {
      setDetectingGaps(false);
    }
  };

  const {
    // Meta / i18n
    show,
    lang,
    t,
    API,
    videoId,
    // Permissions
    canEditKnowledge,
    isAdmin,
    // Search
    transcriptSearch,
    setTranscriptSearch,
    filteredSegments,
    // Sub-tab state
    transcriptSubTab,
    setTranscriptSubTab,
    // Transcripts data
    allTranscripts,
    setAllTranscripts,
    transcriptsLoading,
    loadAllTranscripts,
    // Fusion & report modal
    fusionBlockedReason,
    fusionAvailable,
    importBlockedReason,  // Vincent B751 — bloque UNIQUEMENT si Whisper failed
    openReportModal,
    // Cross-validation
    crossValidating,
    setCrossValidating,
    crossValidateText,
    setCrossValidateText,
    crossValidateResult,
    setCrossValidateResult,
    pollMergedTranscript,
    // Speaker map
    speakerMap,
    // Vincent B901 (Eric 2026-02-14) — Stats par locuteur pour la légende
    // (« N tours · X,X min »). Calculées côté VideoDetailPage à partir de
    // la même source que speakerMap.
    speakerStats,
    // Vincent B920 + B921 (Eric 2026-02-14 SPEC STRICK PROD) — Boîte
    // repliable multi-source pour la légende des locuteurs. Voir
    // SpeakerLegendBox.js pour la logique de rendu.
    speakerColorMap,
    speakerStatsBySource,
    sourcesPresentes,
    segmentsBySource,
    seuilDivergencePct,
    onSeuilChange,
    speakerLegendCollapsed,
    setSpeakerLegendCollapsed,
    // Vincent B694 — locuteurs sélectionnables par phrase (options du menu déroulant)
    speakerOptions,
    // Vincent B696-enhancement — callback pour recharger la banque après
    // création d'un nouveau locuteur via le mini-modal SpeakerCell (Whisper).
    reloadSpeakerData,
    // Whisper sub-tab props
    setShowHistory,
    loadRevisions,
    loadVideoData,
    setUrlProposals,
    urlValidating,
    setUrlValidating,
    startPolling,
    useDiarisation,
    useFacialId,
    transcriptContainerRef,
    data,
    activeIdx,
    seekTo,
    activeSegmentRef,
    highlightText,
    isProcessing,
    formatTimeBracket,
    // Fireflies sub-tab props
    currentTime,
    parseTextTranscriptToSegments,
    // Merged sub-tab props
    urlProposals,
    urlApplying,
    urlDropdownSelections,
    punctuationLoading,
    punctuationProposal,
    rejectedCorrections,
    editingCorrection,
    bulkMinorBusy,
    setUrlApplying,
    setUrlDropdownSelections,
    setPunctuationLoading,
    setPunctuationProposal,
    setAcceptedPunctMarks,
    setRejectedPunctMarks,
    setRejectedCorrections,
    setEditingCorrection,
    setBulkMinorBusy,
  } = props;

  // Tâche 1396 — Après une fusion ou une restauration de locuteurs, les
  // DEUX sources de la légende doivent repartir du serveur : la colonne
  // Whisper vient de `loadVideoData`, les colonnes Fireflies et Fusionnée de
  // `loadAllTranscripts`. N'en recharger qu'une laissait l'écran affirmer
  // l'inverse de ce que le serveur venait de faire.
  //
  // Tâche 1418 (Eric 2026-09-30) — L'onglet Locuteurs lit `speaker_mapping`
  // par son propre appel : sans lui, la fusion réalignait la base mais
  // l'écran gardait l'ancienne attribution jusqu'au rechargement de la page.
  const rechargerLocuteurs = useCallback(async () => {
    await Promise.all([
      typeof loadVideoData === "function" ? loadVideoData() : null,
      typeof loadAllTranscripts === "function" ? loadAllTranscripts() : null,
      typeof reloadSpeakerData === "function" ? reloadSpeakerData() : null,
    ]);
  }, [loadVideoData, loadAllTranscripts, reloadSpeakerData]);

  // Vincent B755 (Eric 2026-02-17) — Modal « Réglages fusion » : cases
  // Whisper / Fireflies. Hooks déclarés AVANT tout early-return.
  const [showFusionSettings, setShowFusionSettings] = useState(false);
  // Tâche 1258b (Eric 2026-06-28) — Le mode de fusion se choisit AU MOMENT de
  // lancer : vraie fusion (2 sources) ou copie fidèle d'une seule source. Le
  // choix était enterré dans « Réglages fusion » et le bouton principal
  // forçait toujours les 2 sources.
  const [fusionMenuOpen, setFusionMenuOpen] = useState(false);
  // Vincent B939 (Eric 2026-02-14 SPEC STRICK PROD) — Modal bulk apply
  // corrections orthographiques (liste multi-select).
  const [showPendingCorrections, setShowPendingCorrections] = useState(false);
  // Vincent B758/B763 (Eric 2026-02-17) — Persistance défauts avec
  // chaîne de priorité complète :
  //   1. user.preferences.fusion_sources_default (choix perso)
  //   2. user.org_fusion_sources_default (défaut org fixé par admin)
  //   3. video.fusion_sources (dernier choix pour cette vidéo)
  //   4. ["whisper", "fireflies"] (fallback historique)
  const { user, refreshUser } = useAuth();
  const _userDefault = user?.preferences?.fusion_sources_default;
  const _orgDefault = user?.org_fusion_sources_default;
  const _videoDefault = data?.video?.fusion_sources;
  const _pickDefault = () => {
    if (Array.isArray(_userDefault) && _userDefault.length > 0) return _userDefault;
    if (Array.isArray(_orgDefault) && _orgDefault.length > 0) return _orgDefault;
    if (Array.isArray(_videoDefault) && _videoDefault.length > 0) return _videoDefault;
    return ["whisper", "fireflies"];
  };
  const _defaultSources = _pickDefault();
  const [fusionSources, setFusionSources] = useState(_defaultSources);
  const _keyForSync = JSON.stringify([_userDefault, _orgDefault, _videoDefault]);
  useEffect(() => {
    setFusionSources(_pickDefault());
  }, [_keyForSync]);
  const [refusing, setRefusing] = useState(false);
  // Vincent B761 (Eric 2026-02-17) — Pagination de l'aperçu diff.
  const [previewPage, setPreviewPage] = useState(0);
  const _previewPageSize = 3;
  // Vincent B767 (Eric 2026-02-17) — Threshold Jaccard configurable.
  // Défaut 0.55 (B764). Slider [0.20 → 0.90] pas 0.05.
  const [jaccardThreshold, setJaccardThreshold] = useState(0.55);
  useEffect(() => {
    if (showFusionSettings) setPreviewPage(0);
  }, [showFusionSettings, fusionSources.length]);

  // Vincent B900 (Eric 2026-02-14 SPEC STRICK PROD) — Filtre par locuteur
  // déclenché par un clic sur les chips colorés de la légende.
  //   • `speakerFilter === null` → aucun filtre (comportement historique).
  //   • `speakerFilter === "<nom>"` → n'affiche QUE les tours de parole
  //     de ce locuteur dans les 3 sous-onglets (Whisper / Fireflies /
  //     Fusionnée). Re-cliquer la même puce l'efface (toggle).
  //   • Reset automatique quand la source change ou quand la puce
  //     sélectionnée n'existe plus dans la nouvelle source (sinon on
  //     afficherait « 0 tour » sans que l'utilisateur ne sache pourquoi).
  const [speakerFilter, setSpeakerFilter] = useState(null);
  useEffect(() => {
    if (speakerFilter && !(speakerFilter in (speakerMap || {}))) {
      setSpeakerFilter(null);
    }
  }, [speakerMap, speakerFilter]);
  const toggleSpeakerFilter = (name) => {
    setSpeakerFilter((cur) => (cur === name ? null : name));
  };
  const clearSpeakerFilter = () => setSpeakerFilter(null);

  if (!show) return null;

  const handleFusionSettingsSubmit = async () => {
    if (fusionSources.length === 0) {
      toast.error(lang === "fr" ? "Sélectionnez au moins une source." : "Select at least one source.");
      return;
    }
    setRefusing(true);
    try {
      // B758 — Persiste le choix comme défaut utilisateur (best-effort, non bloquant)
      axios.patch(
        `${API}/api/auth/me/preferences`,
        { fusion_sources_default: [...fusionSources].sort() },
        { withCredentials: true },
      ).then(() => {
        if (typeof refreshUser === "function") refreshUser();
      }).catch(() => { /* ignore */ });

      const { data: respData } = await axios.post(
        `${API}/api/videos/${videoId}/re-merge?sources=${encodeURIComponent(fusionSources.join(","))}`,
        {},
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `Fusion relancée (sources : ${respData.sources?.join(" + ") || fusionSources.join(" + ")}). L'onglet « Fusionnée » sera mis à jour dans 1-3 min.`
          : `Fusion re-launched (sources: ${respData.sources?.join(" + ") || fusionSources.join(" + ")}).`,
      );
      setShowFusionSettings(false);
      if (typeof loadAllTranscripts === "function") await loadAllTranscripts();
      if (typeof pollMergedTranscript === "function") pollMergedTranscript();
    } catch (e) {
      // Tâche 1417 (Eric 2026-09-30) — « Tu indiques : la source n'a pas
      // changé, relancez avec confirmation… mais tu ne donnes aucun moyen de
      // relancer avec confirmation ! »
      //
      // Le serveur refuse (409) une fusion identique parce qu'elle coûte des
      // appels IA pour rien (tâche 1258b). Ce refus était affiché comme une
      // erreur sèche : la demande de confirmation n'était posée nulle part.
      // Elle l'est maintenant, ici, et la reprise part avec `force=1`.
      const refusUtile = e?.response?.status === 409;
      if (refusUtile) {
        const ok = await confirmDialog(
          lang === "fr"
            ? "La source n'a pas changé depuis la dernière fusion : refaire le travail donnera exactement le même résultat, au prix d'appels IA facturés. Relancer quand même ?"
            : "The source has not changed since the last merge: re-running will produce exactly the same result, at the cost of billed AI calls. Run anyway?",
          {
            tone: "warning",
            confirmLabel: lang === "fr" ? "Relancer quand même" : "Run anyway",
          },
        );
        if (!ok) return;
        try {
          const { data: forceData } = await axios.post(
            `${API}/api/videos/${videoId}/re-merge?sources=${encodeURIComponent(fusionSources.join(","))}&force=1`,
            {},
            { withCredentials: true },
          );
          toast.success(
            lang === "fr"
              ? `Fusion relancée (sources : ${forceData.sources?.join(" + ") || fusionSources.join(" + ")}). L'onglet « Fusionnée » sera mis à jour dans 1-3 min.`
              : `Fusion re-launched (sources: ${forceData.sources?.join(" + ") || fusionSources.join(" + ")}).`,
          );
          setShowFusionSettings(false);
          if (typeof loadAllTranscripts === "function") await loadAllTranscripts();
          if (typeof pollMergedTranscript === "function") pollMergedTranscript();
          return;
        } catch (e2) {
          toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e2.response?.data?.detail || e2.message));
          return;
        }
      }
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
    } finally {
      setRefusing(false);
    }
  };

  // Vincent B945v2 (Eric 2026-02-14 SPEC STRICK PROD) — Extraction des
  // boutons « Refaire la fusion » et « Réglages fusion » en variables
  // partagées. Objectif : les rendre UNE FOIS et les distribuer soit
  // dans Line 2 (sous-onglet Fusionnée), soit dans la toolbar SubTab
  // (Whisper/Fireflies via prop `extraFusionButtons`). Résultat : un
  // seul bloc visuel de boutons dans chaque sous-onglet, respectant
  // l'ordre exigé par l'utilisateur :
  //   Whisper/Fireflies : [..., Refaire la fusion, Réglages fusion, URLs, Historique]
  //   Merged            : [..., Réglages fusion, Diagnostic fusion, Rapport fusion, URLs, Historique]
  const canShowFusionButtons = !!allTranscripts?.summary?.has_whisper
    && !!allTranscripts?.summary?.has_fireflies;

  // Tâche 1258b — Lancement de la fusion avec le mode CHOISI.
  //   • 2 sources  → vraie fusion (comparaison par blocs sur la timeline Whisper)
  //   • 1 source   → copie fidèle (aucun appel IA, texte et horodatages intacts)
  // Un pré-contrôle (`dry_run`) dit si la source a changé depuis la dernière
  // fusion : refaire un travail identique coûtait des appels IA pour rien.
  const runFusion = async (sources) => {
    setFusionMenuOpen(false);
    if (crossValidating || fusionBlockedReason) return;
    const hasMerged = !!allTranscripts?.summary?.has_merged;
    const mono = sources.length === 1;
    const srcLabel = sources[0] === "whisper" ? "Whisper" : "Fireflies";
    const query = `sources=${encodeURIComponent(sources.join(","))}`;

    let unchanged = false;
    let lastAt = null;
    try {
      const { data } = await axios.post(
        `${API}/api/videos/${videoId}/re-merge?${query}&dry_run=1`,
        {},
        { withCredentials: true },
      );
      unchanged = !!data?.unchanged;
      lastAt = data?.last_fusion_at || null;
    } catch {
      // Le pré-contrôle est un confort : jamais un blocage.
    }

    const whenLabel = lastAt
      ? new Date(lastAt).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })
      : "";
    const message = unchanged
      ? (lang === "fr"
          ? `La source n'a pas changé depuis la dernière fusion${whenLabel ? ` du ${whenLabel}` : ""}. Refaire le travail donnera exactement le même résultat. Relancer quand même ?`
          : `The source has not changed since the last merge${whenLabel ? ` (${whenLabel})` : ""}. Re-running will produce the same result. Run anyway?`)
      : mono
        ? (lang === "fr"
            ? `Copier la transcription ${srcLabel} telle quelle dans la version fusionnée ? Aucune comparaison n'est faite : texte, horodatages et locuteurs sont conservés à l'identique, sans appel à l'IA.`
            : `Copy the ${srcLabel} transcript as-is into the merged version? No comparison, no AI call.`)
        : (lang === "fr"
            ? "Générer la fusion à partir de Whisper + Fireflies ? Cela crée la transcription canonique utilisée par Q&R / Scripts / Procédures."
            : "Generate the merged transcript from Whisper + Fireflies?");
    const ok = await confirmDialog(message, {
      tone: "warning",
      confirmLabel: hasMerged
        ? (lang === "fr" ? "Régénérer" : "Regenerate")
        : (lang === "fr" ? "Générer" : "Generate"),
    });
    if (!ok) return;

    try {
      setCrossValidating(true);
      setCrossValidateResult(null);
      const { data: respData } = await axios.post(
        `${API}/api/videos/${videoId}/re-merge?${query}&force=1`,
        {},
        { withCredentials: true },
      );
      setCrossValidateResult(respData);
      toast.success(
        lang === "fr"
          ? (mono
              ? `Copie de ${srcLabel} lancée (aucun appel IA). Onglet « Fusionnée » mis à jour dans quelques secondes.`
              : "Fusion lancée en arrière-plan. Onglet « Fusionnée » mis à jour dans 1-3 min.")
          : (mono
              ? `${srcLabel} copy launched (no AI call).`
              : "Merge launched in background. 'Merged' tab will update in 1-3 min."),
      );
      if (typeof loadAllTranscripts === "function") await loadAllTranscripts();
      if (typeof pollMergedTranscript === "function") pollMergedTranscript();
      setTranscriptSubTab("merged");
    } catch (e) {
      toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
    } finally {
      setCrossValidating(false);
    }
  };

  const _hasWhisper = !!allTranscripts?.summary?.has_whisper;
  const _hasFireflies = !!allTranscripts?.summary?.has_fireflies;
  const fusionModes = [
    {
      id: "real",
      sources: ["whisper", "fireflies"],
      label: lang === "fr" ? "Fusion réelle — Whisper + Fireflies" : "Real merge — Whisper + Fireflies",
      hint: lang === "fr"
        ? "Compare les deux transcriptions par blocs sur la ligne de temps Whisper. Le plus long, et ce que Whisper n'a pas entendu reste absent."
        : "Compares both transcripts block by block on the Whisper timeline.",
      enabled: _hasWhisper && _hasFireflies,
      why: lang === "fr" ? "Nécessite Whisper ET Fireflies." : "Requires both Whisper AND Fireflies.",
    },
    {
      id: "fireflies-only",
      sources: ["fireflies"],
      label: lang === "fr" ? "Copie fidèle — Fireflies seul" : "Faithful copy — Fireflies only",
      hint: lang === "fr"
        ? "Reprend Fireflies à l'identique : rien n'est réinterprété, aucun appel à l'IA."
        : "Exact copy of Fireflies: nothing reinterpreted, no AI call.",
      enabled: _hasFireflies,
      why: lang === "fr" ? "Aucune transcription Fireflies importée." : "No Fireflies transcript imported.",
    },
    {
      id: "whisper-only",
      sources: ["whisper"],
      label: lang === "fr" ? "Copie fidèle — Whisper seul" : "Faithful copy — Whisper only",
      hint: lang === "fr"
        ? "Reprend Whisper à l'identique : utile quand Fireflies est mauvais ou absent."
        : "Exact copy of Whisper: useful when Fireflies is poor or missing.",
      enabled: _hasWhisper,
      why: lang === "fr" ? "Aucune transcription Whisper disponible." : "No Whisper transcript available.",
    },
  ];

  const refaireFusionBtn = (
    <div key="refaire-fusion" className="relative inline-block">
      <button
        type="button"
        disabled={crossValidating || (!_hasWhisper && !_hasFireflies) || !!fusionBlockedReason}
        onClick={() => setFusionMenuOpen((open) => !open)}
        className={`${GROUP_RERUN} disabled:opacity-50 disabled:cursor-not-allowed`}
        title={(!_hasWhisper && !_hasFireflies)
          ? (lang === "fr"
              ? "Aucune transcription disponible. Transcrivez la vidéo ou importez Fireflies."
              : "No transcript available yet. Transcribe the video or import Fireflies.")
          : (fusionBlockedReason || (lang === "fr"
              ? "Choisir le mode : vraie fusion des deux sources, ou copie fidèle d'une seule source."
              : "Choose the mode: real merge of both sources, or faithful copy of a single source."))}
        data-testid="generate-fusion-btn"
      >
        {/* Tâche 1348 (Eric 2026-06-30) — « Refaire » ne disait pas ce qu'il
            fait quand rien n'a encore été fusionné. Le verbe suit donc l'état
            réel du document : Fusionner d'abord, Refusionner ensuite. */}
        {crossValidating ? "⏳" : "🔀"} {lang === "fr"
          ? (allTranscripts?.summary?.has_merged ? "Refusionner" : "Fusionner")
          : (allTranscripts?.summary?.has_merged ? "Re-merge" : "Merge")} ▾
      </button>
      {fusionMenuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setFusionMenuOpen(false)} />
          <div
            /* Tâche 1354 (Eric 2026-06-30) — 320 px coupaient « Fusion réelle
               — Whisper + Fireflies » et ses explications. Largeur portée à
               480 px, bornée à la fenêtre pour ne pas déborder à 390 px, et
               `whitespace-normal` explicite : la barre d'outils parente passe
               ses boutons en `whitespace-nowrap`, qui se transmettait ici. */
            className="absolute left-0 top-full mt-1 z-50 w-[30rem] max-w-[calc(100vw-2rem)] rounded-md border border-zinc-200 bg-white shadow-lg py-1 text-left whitespace-normal"
            data-testid="fusion-mode-menu"
          >
            {fusionModes.map((mode) => (
              <button
                key={mode.id}
                type="button"
                disabled={!mode.enabled}
                onClick={() => runFusion(mode.sources)}
                title={mode.enabled ? mode.hint : mode.why}
                className="w-full px-3 py-2 text-left whitespace-normal hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`fusion-mode-${mode.id}`}
              >
                <span className="block text-xs font-semibold text-zinc-900 whitespace-normal break-words">{mode.label}</span>
                <span className="block text-[11px] text-zinc-500 leading-snug mt-0.5 whitespace-normal break-words">
                  {mode.enabled ? mode.hint : mode.why}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const reglagesFusionBtn = (
    <button
      key="reglages-fusion"
      type="button"
      disabled={!canShowFusionButtons}
      onClick={() => canShowFusionButtons && setShowFusionSettings(true)}
      className={`${GROUP_SETTINGS} disabled:opacity-50 disabled:cursor-not-allowed`}
      title={!canShowFusionButtons
        ? (lang === "fr"
            ? "Nécessite les transcriptions Whisper ET Fireflies pour être utile."
            : "Requires both Whisper AND Fireflies transcripts to be useful.")
        : (lang === "fr"
            ? "Choisir les sources utilisées par la fusion (Whisper, Fireflies ou les deux)"
            : "Choose the sources used by the fusion (Whisper, Fireflies or both)")}
      data-testid="fusion-settings-btn"
      data-button-group="settings"
      data-visible-in-subtab="whisper,fireflies,merged"
    >
      ⚙️ {lang === "fr" ? "Réglages" : "Settings"}
    </button>
  );

  // Vincent B970 (Eric 2026-02-14 SPEC STRICK PROD) — Ordre pour les
  // sous-onglets Whisper et Fireflies : [Réglages fusion, Refaire la fusion,
  // URLs, Historique]. Les 2 boutons sont TOUJOURS rendus, disabled si
  // !has_whisper || !has_fireflies (au lieu d'être masqués).
  // Tâche 1307 (Eric 2026-06-30) — Le mot « Fusion : » ouvre le bloc : les
  // boutons ne portent plus « fusion » dans leur libellé.
  const whisperFirefliesFusionSlot = (
    <>
      <ToolbarGroupLabel
        testid="toolbar-group-label-fusion"
        collapsed={isCollapsed("fusion")}
        onToggle={() => toggleGroup("fusion")}
        count={2}
      >
        {lang === "fr" ? "Fusion :" : "Fusion:"}
      </ToolbarGroupLabel>
      {!isCollapsed("fusion") && (
        <>
          {reglagesFusionBtn}
          {refaireFusionBtn}
        </>
      )}
    </>
  );

  // Vincent B970 (Eric 2026-02-14 SPEC STRICK PROD) — Bouton « Remplacer
  // transcription Fireflies » extrait pour être injecté dans la toolbar
  // FirefliesSubTab (première position). Auparavant dans Line 2 → 2 blocs.
  const replaceFirefliesBtn = (
    <button
      key="replace-fireflies"
      type="button"
      onClick={async () => {
        const hasFireflies = !!allTranscripts?.summary?.has_fireflies;
        if (hasFireflies) {
          const ok = await confirmDialog(
            lang === "fr"
              ? "Remplacer la transcription Fireflies actuelle ?\n\nCette action supprime la version Fireflies + la version fusionnée. Whisper et la diarisation sont conservés (aucun crédit AI recalculé). Vous pourrez ensuite importer un nouveau fichier."
              : "Replace current Fireflies transcript?\n\nThis removes the Fireflies + merged versions. Whisper + diarization are kept (no AI credits re-spent). You can then upload a new file.",
          );
          if (!ok) return;
          try {
            await axios.delete(`${API}/api/videos/${videoId}/fireflies-transcript`, { withCredentials: true });
            setAllTranscripts((prev) => prev ? {
              ...prev,
              fireflies: null,
              merged: null,
              summary: { ...prev.summary, has_fireflies: false, has_merged: false },
            } : prev);
          } catch (e) {
            toast.error((lang === "fr" ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
            return;
          }
        }
        setTranscriptSubTab("fireflies");
      }}
      className={GROUP_UTIL}
      title={lang === "fr"
        ? "Ouvre le panneau d'import Fireflies. Supprime d'abord la transcription actuelle si présente."
        : "Opens the Fireflies import panel. Removes the current transcript first if present."}
      data-testid="replace-fireflies-btn"
      data-button-group="util"
      data-visible-in-subtab="fireflies"
    >
      📥 {lang === "fr"
        ? (allTranscripts?.summary?.has_fireflies ? "Remplacer" : "Importer")
        : (allTranscripts?.summary?.has_fireflies ? "Replace" : "Import")}
    </button>
  );

  return (
    <div>
      {/* Tâche 1142 — Avertissement « transcription Gemini, alignement
          approximatif ». Placé tout en haut : c'est une mise en garde de
          lecture, elle doit précéder le texte, pas le suivre. */}
      <TranscriptionProviderNotice video={data?.video} />
      {/* Vincent B935 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Layout
          single-line : le sélecteur Transcription + la case canonique
          + la barre de recherche sont regroupés sur une seule ligne
          (avec wrap sur écrans étroits). Les actions descendent en
          ligne 2 (bloc wrap unique). Pour les non-éditeurs, seule la
          barre de recherche reste visible en haut. */}
      {canEditKnowledge ? null : (
        <TranscriptSearchBar
          value={transcriptSearch}
          onChange={setTranscriptSearch}
          resultCount={filteredSegments.length}
          placeholder={t("search_transcript")}
        />
      )}

      {/* Vincent 2026-05-07 (bug #52, #54, #55) — Menu déroulant unifié remplace les 3 sous-onglets.
          Vincent #355 (Eric 2026-06-21 SPEC STRICK 3e passe) — Pour un non-éditeur,
          on force le sous-onglet « Fusionnée » et on cache le sélecteur de source. */}
      {canEditKnowledge ? (
        <div className="flex flex-col gap-2 border-b border-zinc-100 bg-zinc-50/50 px-3 py-2" data-testid="transcript-source-selector">
          {gapsOpen && (
            <CoverageGapsModal
              videoId={videoId}
              seekTo={seekTo}
              onFilled={loadAllTranscripts}
              onClose={() => setGapsOpen(false)}
            />
          )}
          {/* Vincent B935 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Ligne 1 :
              Label + dropdown + case Source canonique + recherche (single-line).
              Les actions sont sur la ligne 2 (bloc unique wrap). */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="transcript-toolbar-line1">
            {/* Tâche 1310 (Eric 2026-06-30) — « Source : » et la case
                « Canonique » empilées en deux petites lignes de la hauteur du
                menu déroulant : la case occupait la largeur d'un bouton alors
                que le champ de recherche était devenu trop étroit. */}
            <div className="flex flex-col justify-center leading-tight flex-shrink-0">
              <label className="text-xs font-medium text-zinc-700 whitespace-nowrap">
                {lang === "fr" ? "Source :" : "Source:"}
              </label>
              {(() => {
                const src = transcriptSubTab; // whisper|fireflies|merged
                const currentCanon = data?.video?.canonical_transcript_source
                  || (allTranscripts?.summary?.has_merged ? "merged" : "whisper");
                const isCanon = currentCanon === src;
                // Ne permet de cocher que si la source affichée existe réellement
                const sourceAvailable =
                  (src === "whisper" && allTranscripts?.summary?.has_whisper)
                  || (src === "fireflies" && allTranscripts?.summary?.has_fireflies)
                  || (src === "merged" && allTranscripts?.summary?.has_merged);
                const disabled = !sourceAvailable;
                return (
                  <label
                    className={`inline-flex items-center gap-1 text-[10px] font-medium whitespace-nowrap ${
                      isCanon ? "text-teal-700" : "text-zinc-500"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:text-teal-700"}`}
                    data-testid="canonical-source-checkbox-label"
                    data-canonical-source={currentCanon}
                    title={
                      isCanon
                        ? (lang === "fr"
                            ? `« ${src} » est la source canonique de cette vidéo (utilisée par Q&R / Scripts / Procédures / recherche RAG).`
                            : `"${src}" is the canonical source for this video.`)
                        : disabled
                          ? (lang === "fr" ? "Source non disponible" : "Source not available")
                          : (lang === "fr"
                              ? `Cocher pour définir « ${src} » comme source canonique de cette vidéo.`
                              : `Check to set "${src}" as canonical source.`)
                    }
                  >
                    <input
                      type="checkbox"
                      checked={isCanon}
                      disabled={disabled}
                      onChange={async (e) => {
                        const wantCanon = e.target.checked;
                        if (!wantCanon) {
                          // Décocher n'a pas de sens : il faut EXACTEMENT une
                          // source canonique par vidéo → on ignore.
                          return;
                        }
                        try {
                          await axios.put(
                            `${API}/api/videos/${videoId}/canonical-transcript-source`,
                            { source: src },
                            { withCredentials: true }
                          );
                          toast.success(
                            lang === "fr"
                              ? `✅ « ${src} » définie comme source canonique.`
                              : `✅ "${src}" set as canonical source.`
                          );
                          if (typeof loadVideoData === "function") await loadVideoData();
                        } catch (err) {
                          toast.error(
                            (lang === "fr" ? "Erreur : " : "Error: ") +
                              (err.response?.data?.detail || err.message)
                          );
                        }
                      }}
                      className="h-3 w-3 accent-teal-600 disabled:cursor-not-allowed"
                      data-testid="canonical-source-checkbox"
                      aria-label={lang === "fr" ? "Marquer comme source canonique" : "Mark as canonical source"}
                    />
                    {lang === "fr" ? "Canonique" : "Canonical"}
                  </label>
                );
              })()}
            </div>
            {/* Tâche 1310 — Largeur réduite au minimum (`w-auto`) : le menu
                prenait tout l'espace laissé libre au détriment de la
                recherche.
                Tâche 1453 (Eric 2026-06) — La case « curseurs synchronisés »
                est empilée SOUS ce menu, dans la même colonne et à la même
                taille que « Canonique » (`text-[10px]`), parce que c'est le
                menu qu'elle commente : elle agit au moment où l'on change de
                transcription. Elle remplace l'ancien bouton-pastille, qui
                vivait sous la barre d'outils et se lisait comme une action
                alors que c'est un réglage. */}
            <div className="flex flex-col justify-center leading-tight flex-shrink-0 gap-0.5">
              <select
                value={transcriptSubTab}
                onChange={(e) => {
                  setTranscriptSubTab(e.target.value);
                  if (!allTranscripts) loadAllTranscripts();
                }}
                className="text-xs px-2 py-1.5 border border-zinc-300 rounded-md bg-white focus:ring-2 focus:ring-[#002FA7] focus:border-transparent w-auto"
                data-testid="transcript-source-select"
              >
                <option value="whisper">
                  {`🎙️ Whisper${allTranscripts?.summary?.has_whisper ? " ✓" : ""}`}
                </option>
                {/* Vincent B969 (Eric 2026-02-14 SPEC STRICK PROD) — On retire
                    `disabled` sur les options : l'utilisateur DOIT pouvoir naviguer
                    sur les 3 sous-onglets meme si la transcription est absente,
                    pour voir les boutons de generation/import et l'empty state. */}
                <option value="fireflies">
                  {`🔥 Fireflies ${allTranscripts?.summary?.has_fireflies ? "✓" : (lang === "fr" ? "(non importée)" : "(not imported)")}`}
                </option>
                <option value="merged">
                  {`🔀 ${lang === "fr" ? "Fusionnée" : "Merged"} ${allTranscripts?.summary?.has_merged ? `✓ (${allTranscripts.summary.corrections_count})` : (lang === "fr" ? "(non générée)" : "(not generated)")}`}
                </option>
              </select>
              <label
                className={`inline-flex items-center gap-1 text-[10px] font-medium whitespace-nowrap cursor-pointer ${
                  syncCurseurs ? "text-sky-700" : "text-zinc-500 hover:text-sky-700"
                }`}
                data-testid="sync-curseurs-case-label"
                title={lang === "fr"
                  ? "En changeant de transcription, la nouvelle s'ouvre au même instant que celle que tu quittes. Le lecteur vidéo ne bouge pas."
                  : "When you switch transcript, the new one opens at the same moment as the one you leave. The video player does not move."}
              >
                <input
                  type="checkbox"
                  checked={syncCurseurs}
                  onChange={() => {
                    const suivant = !syncCurseurs;
                    setSyncCurseurs(suivant);
                    ecrireReglageSync(suivant);
                  }}
                  className="h-3 w-3 accent-sky-600"
                  data-testid="sync-curseurs-case"
                />
                {/* ⚠️ Pas de majuscule, demandé par Eric — et l'accord au
                    pluriel qu'il a validé (« curseurs synchronisés »). */}
                {lang === "fr" ? "curseurs synchronisés" : "synced cursors"}
              </label>
            </div>
            <ToolbarSeparator />
            {/* Tâches 1270 · 1286 · 1310 (Eric 2026-06-29 / 2026-06-30) — Le
                mot « Écarts : » porte ce que les deux boutons ne répètent
                plus. « Détecter » REFAIT le calcul (il relit trois
                transcriptions) ; « Diagnostic » LIT le dernier calcul, donc
                il est gratuit et instantané. */}
            <ToolbarGroupLabel
              testid="toolbar-group-label-ecarts"
              collapsed={isCollapsed("ecarts")}
              onToggle={() => toggleGroup("ecarts")}
              count={2}
            >
              {lang === "fr" ? "Écarts :" : "Gaps:"}
            </ToolbarGroupLabel>
            {!isCollapsed("ecarts") && (
              <>
            <button
              onClick={detectGaps}
              disabled={detectingGaps}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1.5 rounded-md border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100 whitespace-nowrap flex-shrink-0 disabled:opacity-50"
              title={lang === "fr"
                ? "Recalculer les écarts de cette vidéo à partir des transcriptions"
                : "Recompute this video's gaps from the transcripts"}
              data-testid="transcript-detect-gaps-btn"
            >
              {detectingGaps
                ? (lang === "fr" ? "⏳ Détection…" : "⏳ Detecting…")
                : (lang === "fr" ? "🔎 Détecter" : "🔎 Detect")}
            </button>
            <button
              onClick={() => setGapsOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1.5 rounded-md border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 whitespace-nowrap flex-shrink-0"
              title={lang === "fr"
                ? "Lister les passages non couverts (copier ou exporter)"
                : "List uncovered passages (copy or export)"}
              data-testid="transcript-gaps-btn"
            >
              ⚠ Diagnostic
            </button>
              </>
            )}
            {/* Vincent B935 v2 (Eric 2026-02-14 SPEC STRICK PROD) — Barre
                de recherche inline (compact) sur la même ligne que le
                sélecteur et la case canonique. */}
            <TranscriptSearchBar
              value={transcriptSearch}
              onChange={setTranscriptSearch}
              resultCount={filteredSegments.length}
              placeholder={t("search_transcript")}
              compact
            />
          </div>
          {/* Vincent B935 v2 (Eric 2026-02-14) — Ligne 2 : bloc unique
              wrap de toutes les actions par sous-onglet.
              Style aligné sur WhisperToolbar (text-[11px] font-semibold px-3
              py-1 rounded inline-flex items-center gap-1.5). */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="transcript-toolbar-line2">
          {/* Tâche 1270 — Le bouton « Écarts » vit maintenant sur la ligne 1
              (voir plus haut) : il n'est plus dupliqué ici. */}
          {/* Vincent B972 + B973 (Eric 2026-02-26 SPEC STRICK PROD) —
              Barre d'actions du sous-onglet Fusionnée extraite dans
              MergedToolbar (parité architecture WhisperToolbar). Ordre
              exact et separateurs verrouilles dans MergedToolbar. */}
          <MergedToolbar
            show={transcriptSubTab === "merged"}
            canEditKnowledge={canEditKnowledge}
            lang={lang}
            api={API}
            videoId={videoId}
            allTranscripts={allTranscripts}
            reglagesFusionBtn={reglagesFusionBtn}
            refaireFusionBtn={refaireFusionBtn}
            crossValidating={crossValidating}
            setCrossValidating={setCrossValidating}
            loadAllTranscripts={loadAllTranscripts}
            loadVideoData={loadVideoData}
            openReportModal={openReportModal}
            setShowPendingCorrections={setShowPendingCorrections}
            setUrlValidating={setUrlValidating}
            setUrlProposals={setUrlProposals}
            urlValidating={urlValidating}
            setShowHistory={setShowHistory}
            loadRevisions={loadRevisions}
          />
          </div>
          {/* Vincent B896 (Eric 2026-02-14 SPEC STRICK PROD) — L'ancienne
              ligne dédiée « Réglages fusion » (visible uniquement sur le
              sous-onglet Fusionnée) a été RETIRÉE : le bouton est
              désormais inline à côté de « Refaire la fusion », visible
              pour les 3 sources (voir bloc ci-dessus). */}
          {/* Tâche 1310 (Eric 2026-06-30) — La mention « … = source
              canonique » vit maintenant dans l'en-tête de la vidéo, après la
              chip « Parcours » (format « SOURCE · fusionnée »). Elle n'a rien
              à faire sous les boutons : c'est une information de la fiche,
              pas une action. */}
        </div>
      ) : null}

      {/* Cross-validation Fireflies — only on Fireflies sub-tab when no Fireflies imported yet.
          Vincent B970 : reste visible pour permettre l'import via drag-and-drop.
          Le bouton « Remplacer transcription Fireflies » de la toolbar declenche
          le meme flow (suppression eventuelle + setTranscriptSubTab). */}
      <FirefliesImportPanel
        show={isAdmin && transcriptSubTab === "fireflies" && !allTranscripts?.fireflies}
        fusionBlockedReason={fusionBlockedReason}
        importBlockedReason={importBlockedReason}
        lang={lang}
        api={API}
        videoId={videoId}
        crossValidating={crossValidating}
        setCrossValidating={setCrossValidating}
        crossValidateText={crossValidateText}
        setCrossValidateText={setCrossValidateText}
        crossValidateResult={crossValidateResult}
        setCrossValidateResult={setCrossValidateResult}
        loadAllTranscripts={loadAllTranscripts}
        setTranscriptSubTab={setTranscriptSubTab}
        pollMergedTranscript={pollMergedTranscript}
      />

      {/* Speaker Legend
          Vincent B900 (Eric 2026-02-14 SPEC STRICK PROD) — Chaque puce
          colorée devient un bouton toggle : cliquer filtre le transcript
          aux tours de parole du locuteur choisi ; re-cliquer efface le
          filtre. Le nom SÉLECTIONNÉ est encadré + fond de sa couleur
          (safe daltonien : bordure épaisse + label check ✓, pas
          uniquement la couleur). */}
      {/* Vincent B920 + B921 (Eric 2026-02-14 SPEC STRICK PROD) —
          Nouvelle boîte repliable multi-source qui remplace l'ancienne
          rangée de chips. Voir SpeakerLegendBox.js pour le détail :
            • conteneur avec triangle collapse en haut à droite (B920)
            • tableau Locuteur × Source (B921), 4 colonnes, avec les
              2 sources non-actives grisées + noms/lignes des locuteurs
              absents grisés & italique.
          L'état `speakerLegendCollapsed` vit dans VideoDetailPage donc
          persiste au switch de sous-onglet — exactement le comportement
          demandé (« ouvert/fermé quel que soit le sous-onglet »). */}
      {/* Vincent B954 (Eric 2026-02-14 SPEC STRICK PROD) — Toolbars des
          SubTabs Whisper/Fireflies rendus AU-DESSUS du tableau des
          locuteurs, dans le même bloc que les boutons de fusion. Cela
          uniformise le layout avec MergedSubTab (single-block) : tous
          les boutons dans un seul bandeau homogène au-dessus de la
          table SpeakerLegendBox. Les segments restent en-dessous
          (rendu séparé avec `render="segments"`). */}
      <WhisperSubTab
        show={transcriptSubTab === "whisper"}
        render="toolbar"
        canEditKnowledge={canEditKnowledge}
        lang={lang}
        api={API}
        videoId={videoId}
        setShowHistory={setShowHistory}
        loadRevisions={loadRevisions}
        loadVideoData={loadVideoData}
        loadAllTranscripts={loadAllTranscripts}
        setUrlProposals={setUrlProposals}
        urlValidating={urlValidating}
        setUrlValidating={setUrlValidating}
        startPolling={startPolling}
        useDiarisation={useDiarisation}
        useFacialId={useFacialId}
        allTranscripts={allTranscripts}
        isProcessing={isProcessing}
        extraFusionButtons={whisperFirefliesFusionSlot}
      />
      <FirefliesSubTab
        show={transcriptSubTab === "fireflies"}
        render="toolbar"
        canEdit={canEditKnowledge}
        isAdmin={isAdmin}
        lang={lang}
        api={API}
        videoId={videoId}
        allTranscripts={allTranscripts}
        setAllTranscripts={setAllTranscripts}
        transcriptsLoading={transcriptsLoading}
        setUrlProposals={setUrlProposals}
        urlValidating={urlValidating}
        setUrlValidating={setUrlValidating}
        setShowHistory={setShowHistory}
        loadRevisions={loadRevisions}
        loadVideoData={loadVideoData}
        loadAllTranscripts={loadAllTranscripts}
        extraFusionButtons={whisperFirefliesFusionSlot}
        replaceFirefliesBtn={replaceFirefliesBtn}
      />

      {/* Tâche 1396 (Eric 2026-09-29) — « Tu me dis 3 segments renommés,
          mais la légende ne bouge pas, puis tu refuses de refusionner. »
          CAUSE : après la fusion on ne rechargeait QUE `allTranscripts`
          (Fireflies + Fusionnée). La colonne WHISPER de la légende vient de
          `data.segments`, c'est-à-dire de `loadVideoData` — jamais rappelé.
          Le serveur avait bien renommé ; l'écran montrait l'état d'avant, et
          le deuxième essai répondait « aucun segment renommé » puisqu'il n'y
          avait plus rien à renommer. Les DEUX sources se rechargent
          désormais, et en parallèle : elles ne dépendent pas l'une de
          l'autre. */}
      {Object.keys(speakerColorMap || {}).length > 0 && (
        <SpeakerLegendBox
          lang={lang}
          activeSubTab={transcriptSubTab}
          speakerColorMap={speakerColorMap}
          speakerStatsBySource={speakerStatsBySource}
          sourcesPresentes={sourcesPresentes}
          segmentsBySource={segmentsBySource}
          seuilDivergencePct={seuilDivergencePct}
          onSeuilChange={onSeuilChange}
          onSeek={seekTo}
          speakerFilter={speakerFilter}
          toggleSpeakerFilter={toggleSpeakerFilter}
          collapsed={speakerLegendCollapsed}
          setCollapsed={setSpeakerLegendCollapsed}
          videoId={videoId}
          canEdit={!!canEditKnowledge}
          onSpeakersMerged={rechargerLocuteurs}
        />
      )}

      {/* Tâche 1390 (Eric 2026-06-30) — Journal des renommages, juste sous la
          légende : c'est là qu'on renomme, donc c'est là qu'on doit pouvoir
          revenir en arrière. Réservé aux personnes qui peuvent éditer — pour
          les autres, une trace nominative sans bouton n'apporte rien. */}
      {canEditKnowledge && (
        <SpeakerRenameLogPanel videoId={videoId} onRestored={rechargerLocuteurs} />
      )}

      {/* Tâche 1444 (Eric 2026-10-02) — « il faut synchroniser automatiquement
          les curseurs pour parcourir les transcriptions ». Le bandeau est posé
          JUSTE AU-DESSUS des listes de tours : c'est là qu'on circule, donc là
          qu'on doit lire ce qui est montré.
          Tâche 1453 — l'interrupteur, lui, est remonté dans la barre d'outils
          sous le menu des trois sources (case « curseurs synchronisés »), et le
          réglage est PARTAGÉ : il n'existe qu'une seule valeur. */}
      <SyncCurseursBandeau
        sousOnglet={transcriptSubTab}
        currentTime={currentTime}
        actif={syncCurseurs}
        nbLignes={
          (transcriptSubTab === "whisper" ? (filteredSegments || []).length
            : transcriptSubTab === "fireflies" ? (allTranscripts?.fireflies?.segments || []).length
              : (allTranscripts?.merged?.segments || []).length)
        }
      />

      {/* Vincent B900 (Eric 2026-02-14 SPEC STRICK PROD) — Segments
          filtrés au niveau wrapper : chaque sous-onglet reçoit
          uniquement les tours de parole du locuteur sélectionné dans
          la légende. Pas de modification requise dans les sous-onglets
          — on ne fait qu'injecter des tableaux déjà filtrés. */}
      {(() => { return null; })()}
      {/* ─── WHISPER segments (sous SpeakerLegendBox) ─── */}
      <WhisperSubTab
        show={transcriptSubTab === "whisper"}
        render="segments"
        canEditKnowledge={canEditKnowledge}
        lang={lang}
        api={API}
        videoId={videoId}
        setShowHistory={setShowHistory}
        loadRevisions={loadRevisions}
        loadVideoData={loadVideoData}
        loadAllTranscripts={loadAllTranscripts}
        setUrlProposals={setUrlProposals}
        urlValidating={urlValidating}
        setUrlValidating={setUrlValidating}
        startPolling={startPolling}
        useDiarisation={useDiarisation}
        useFacialId={useFacialId}
        transcriptContainerRef={transcriptContainerRef}
        filteredSegments={
          speakerFilter
            ? (filteredSegments || []).filter((s) => isSameSpeakerName(s.speaker, speakerFilter))
            : filteredSegments
        }
        data={data}
        activeIdx={activeIdx}
        speakerMap={speakerMap}
        speakerOptions={speakerOptions}
        reloadSpeakerData={reloadSpeakerData}
        seekTo={seekTo}
        activeSegmentRef={activeSegmentRef}
        transcriptSearch={transcriptSearch}
        highlightText={highlightText}
        isProcessing={isProcessing}
        allTranscripts={allTranscripts}
        formatTimeBracket={formatTimeBracket}
      />

      {/* ─── FIREFLIES segments (sous SpeakerLegendBox) ─── */}
      <FirefliesSubTab
        show={transcriptSubTab === "fireflies"}
        render="segments"
        canEdit={canEditKnowledge}
        isAdmin={isAdmin}
        lang={lang}
        api={API}
        videoId={videoId}
        allTranscripts={
          speakerFilter && allTranscripts?.fireflies?.segments
            ? {
                ...allTranscripts,
                fireflies: {
                  ...allTranscripts.fireflies,
                  segments: allTranscripts.fireflies.segments.filter(
                    (s) => isSameSpeakerName(s.speaker, speakerFilter),
                  ),
                },
              }
            : allTranscripts
        }
        setAllTranscripts={setAllTranscripts}
        transcriptsLoading={transcriptsLoading}
        urlValidating={urlValidating}
        setUrlValidating={setUrlValidating}
        setUrlProposals={setUrlProposals}
        crossValidating={crossValidating}
        setCrossValidating={setCrossValidating}
        setCrossValidateResult={setCrossValidateResult}
        setShowHistory={setShowHistory}
        loadRevisions={loadRevisions}
        loadVideoData={loadVideoData}
        loadAllTranscripts={loadAllTranscripts}
        pollMergedTranscript={pollMergedTranscript}
        speakerMap={speakerMap}
        currentTime={currentTime}
        activeSegmentRef={activeSegmentRef}
        seekTo={seekTo}
        parseTextTranscriptToSegments={parseTextTranscriptToSegments}
        formatTimeBracket={formatTimeBracket}
      />

      {/* ─── MERGED sub-tab ─── */}
      <MergedSubTab
        show={transcriptSubTab === "merged"}
        canEdit={canEditKnowledge}
        lang={lang}
        api={API}
        videoId={videoId}
        allTranscripts={
          speakerFilter && allTranscripts?.merged?.segments
            ? {
                ...allTranscripts,
                merged: {
                  ...allTranscripts.merged,
                  segments: allTranscripts.merged.segments.filter(
                    (s) => isSameSpeakerName(s.speaker, speakerFilter),
                  ),
                },
              }
            : allTranscripts
        }
        transcriptsLoading={transcriptsLoading}
        crossValidating={crossValidating}
        crossValidateResult={crossValidateResult}
        fusionAvailable={fusionAvailable}
        fusionBlockedReason={fusionBlockedReason}
        urlValidating={urlValidating}
        urlProposals={urlProposals}
        urlApplying={urlApplying}
        urlDropdownSelections={urlDropdownSelections}
        punctuationLoading={punctuationLoading}
        punctuationProposal={punctuationProposal}
        rejectedCorrections={rejectedCorrections}
        editingCorrection={editingCorrection}
        bulkMinorBusy={bulkMinorBusy}
        speakerMap={speakerMap}
        setCrossValidating={setCrossValidating}
        setCrossValidateResult={setCrossValidateResult}
        setUrlValidating={setUrlValidating}
        setUrlProposals={setUrlProposals}
        setUrlApplying={setUrlApplying}
        setUrlDropdownSelections={setUrlDropdownSelections}
        setPunctuationLoading={setPunctuationLoading}
        setPunctuationProposal={setPunctuationProposal}
        setAcceptedPunctMarks={setAcceptedPunctMarks}
        setRejectedPunctMarks={setRejectedPunctMarks}
        setRejectedCorrections={setRejectedCorrections}
        setEditingCorrection={setEditingCorrection}
        setBulkMinorBusy={setBulkMinorBusy}
        loadVideoData={loadVideoData}
        loadAllTranscripts={loadAllTranscripts}
        pollMergedTranscript={pollMergedTranscript}
        seekTo={seekTo}
        formatTimeBracket={formatTimeBracket}
      />

      {/* Vincent B755 (Eric 2026-02-17) — Modal « Réglages fusion » */}
      {/* Vincent B939 (Eric 2026-02-14) — Modal bulk-apply corrections
          orthographiques : liste multi-select des corrections en attente. */}
      <PendingCorrectionsModal
        open={showPendingCorrections}
        onOpenChange={setShowPendingCorrections}
        videoId={videoId}
        mergedSegments={allTranscripts?.merged?.segments || []}
        onApplied={loadAllTranscripts}
        seekTo={seekTo}
        lang={lang}
      />
      {showFusionSettings && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !refusing && setShowFusionSettings(false)}
          data-testid="fusion-settings-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-zinc-900 mb-1">
              {lang === "fr" ? "Réglages fusion" : "Fusion settings"}
            </h3>
            <p className="text-xs text-zinc-600 mb-4">
              {lang === "fr"
                ? "Choisissez les sources utilisées pour construire la transcription fusionnée. Quand une seule source est cochée, l'alignement croisé est sauté et seule la correction grammaire + jargon est appliquée."
                : "Choose the sources used to build the merged transcript. Single-source mode skips cross-alignment and only applies grammar + jargon correction."}
            </p>
            <div className="space-y-2">
              {[
                { key: "whisper", label: "Whisper", available: !!allTranscripts?.summary?.has_whisper },
                { key: "fireflies", label: "Fireflies", available: !!allTranscripts?.summary?.has_fireflies },
              ].map((src) => (
                <label
                  key={src.key}
                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${
                    fusionSources.includes(src.key) ? "border-teal-400 bg-teal-50" : "border-zinc-200 bg-white"
                  } ${!src.available ? "opacity-40 cursor-not-allowed" : "hover:border-teal-300"}`}
                  data-testid={`fusion-source-option-${src.key}`}
                >
                  <input
                    type="checkbox"
                    checked={fusionSources.includes(src.key)}
                    disabled={!src.available}
                    onChange={(e) => {
                      setFusionSources((prev) => (
                        e.target.checked ? [...new Set([...prev, src.key])] : prev.filter((s) => s !== src.key)
                      ));
                    }}
                    className="h-4 w-4 accent-teal-600"
                    data-testid={`fusion-source-checkbox-${src.key}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-zinc-800">{src.label}</div>
                    {!src.available && (
                      <div className="text-[10px] text-zinc-500">
                        {lang === "fr" ? "Non disponible sur cette vidéo" : "Not available on this video"}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Vincent B758/B761 (Eric 2026-02-17) — Aperçu diff paginé.
                Bloc préviewer 3 segments par page (Précédent/Suivant).
                Best-effort (masqué si allTranscripts pas encore chargé).
                Aide Vincent à comparer les sources SUR TOUS les segments
                sans quitter le modal. */}
            {(fusionSources.length > 0 && allTranscripts) && (() => {
              const wSegs = fusionSources.includes("whisper") ? (allTranscripts?.whisper?.segments || []) : [];
              const fSegs = fusionSources.includes("fireflies") ? (allTranscripts?.fireflies?.segments || []) : [];
              const mSegs = (allTranscripts?.merged?.segments || []);
              // Vincent B762/B764 (Eric 2026-02-17) — Alignement intelligent
              // par Jaccard sur mots (au lieu de comparer par index brut).
              // Pour chaque segment Whisper, on cherche la meilleure
              // correspondance Fireflies dans une fenêtre ±5 indexes.
              // Idem pour la fusion actuelle (B765) : chaque segment merged
              // cherche sa meilleure paire dans Whisper ET Fireflies pour
              // repérer où GPT a divergé des 2 sources.
              const _norm = (t) => (t || "").toLowerCase().replace(/[.,;:!?()"']/g, "").replace(/\s+/g, " ").trim();
              const _wordSet = (t) => new Set(_norm(t).split(" ").filter((w) => w.length >= 3));
              const _jaccard = (a, b) => {
                if (a.size === 0 || b.size === 0) return 0;
                let inter = 0;
                for (const w of a) if (b.has(w)) inter += 1;
                return inter / (a.size + b.size - inter);
              };
              const _bestMatch = (source, sourceIdx, targets, window = 5) => {
                if (!source || !targets.length) return { idx: -1, score: 0 };
                const sSet = _wordSet(source.text || source.merged_text);
                if (sSet.size === 0) return { idx: -1, score: 0 };
                let best = { idx: -1, score: 0 };
                const lo = Math.max(0, sourceIdx - window);
                const hi = Math.min(targets.length - 1, sourceIdx + window);
                for (let j = lo; j <= hi; j += 1) {
                  const s = _jaccard(sSet, _wordSet(targets[j].text || targets[j].merged_text));
                  if (s > best.score) best = { idx: j, score: s };
                }
                return best;
              };
              const totalSegs = Math.max(wSegs.length, fSegs.length, mSegs.length);
              const totalPages = Math.max(1, Math.ceil(totalSegs / _previewPageSize));
              const currentPage = Math.min(previewPage, totalPages - 1);
              const startIdx = currentPage * _previewPageSize;
              const endIdx = startIdx + _previewPageSize;
              const rangeLabel = totalSegs === 0
                ? "0"
                : `${startIdx + 1}-${Math.min(endIdx, totalSegs)}`;
              const bothShown = fusionSources.includes("whisper") && fusionSources.includes("fireflies")
                && wSegs.length > 0 && fSegs.length > 0;
              // Build aligned rows for the current page
              const rows = [];
              const seenF = new Set(); // Fireflies déjà appariés
              const seenM = new Set(); // Merged déjà apparié
              const _limit = Math.max(
                wSegs.length ? Math.min(endIdx, wSegs.length) : 0,
                fSegs.length ? Math.min(endIdx, fSegs.length) : 0,
                mSegs.length ? Math.min(endIdx, mSegs.length) : 0,
              );
              // B775 (Eric 2026-02-17 SPEC STRICK PROD) — Détermine la
              // source d'itération : Whisper si présent, sinon Fireflies,
              // sinon Merged. Sans ça, quand Vincent sélectionnait
              // UNIQUEMENT Fireflies, wSegs était vide → _limit = 0 → aucun
              // segment affiché dans la colonne du milieu.
              const _iterSource = wSegs.length > 0 ? "w" : (fSegs.length > 0 ? "f" : "m");
              for (let i = startIdx; i < _limit; i += 1) {
                let w = null, f = null, m = null, fIdx = -1;
                if (_iterSource === "w") {
                  w = wSegs[i] || null;
                  const fMatch = w && fSegs.length ? _bestMatch(w, i, fSegs) : { idx: -1, score: 0 };
                  fIdx = fMatch.idx;
                  f = fIdx >= 0 ? fSegs[fIdx] : null;
                  if (f) seenF.add(fIdx);
                  m = mSegs[i] || null;
                } else if (_iterSource === "f") {
                  // Iter sur Fireflies : chaque segment F cherche son
                  // équivalent Merged par proximité d'index.
                  f = fSegs[i] || null;
                  fIdx = i;
                  if (f) seenF.add(i);
                  m = mSegs[i] || null;
                } else {
                  // Iter sur Merged seul (rare — Whisper et Fireflies non
                  // sélectionnés). On affiche uniquement la colonne de droite.
                  m = mSegs[i] || null;
                }
                if (m) seenM.add(i);
                // Divergences (utilise Jaccard threshold configurable B767)
                const wfSpeakerDiff = bothShown && f && (w.speaker || "").trim() !== (f.speaker || "").trim();
                const wfTextDiff = bothShown && f && _jaccard(_wordSet(w?.text), _wordSet(f?.text)) < jaccardThreshold;
                // B765 : merged vs W et merged vs F
                let mDivFromBoth = false;
                let mSpeakerDiff = false;
                if (m) {
                  const mSet = _wordSet(m.merged_text || m.text);
                  const sim_w = w ? _jaccard(mSet, _wordSet(w.text)) : 0;
                  const sim_f = f ? _jaccard(mSet, _wordSet(f.text)) : 0;
                  mDivFromBoth = (sim_w < jaccardThreshold && sim_f < jaccardThreshold) && mSet.size > 0;
                  const mSpk = (m.speaker || "").trim();
                  if (mSpk && w && f) {
                    mSpeakerDiff = mSpk !== (w.speaker || "").trim() && mSpk !== (f.speaker || "").trim();
                  }
                }
                rows.push({ w, f, m, fIdx, wfSpeakerDiff, wfTextDiff, mDivFromBoth, mSpeakerDiff });
              }
              return (
                <div className="mt-4 border-t border-zinc-200 pt-3" data-testid="fusion-preview-diff">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-2">
                      {lang === "fr"
                        ? `Aperçu aligné (segments ${rangeLabel} / ${totalSegs})`
                        : `Aligned preview (segments ${rangeLabel} / ${totalSegs})`}
                      {bothShown && (
                        <span className="inline-flex items-center gap-1 text-yellow-800 bg-yellow-100 border border-yellow-300 rounded px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal" data-testid="fusion-preview-diff-legend">
                          <span className="inline-block w-2 h-2 rounded-sm bg-yellow-300" />
                          {lang === "fr" ? "= divergence W↔F" : "= divergence W↔F"}
                        </span>
                      )}
                      {mSegs.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-purple-800 bg-purple-100 border border-purple-300 rounded px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal" data-testid="fusion-preview-merged-diff-legend">
                          <span className="inline-block w-2 h-2 rounded-sm bg-purple-300" />
                          {lang === "fr" ? "= fusion GPT diverge" : "= merge GPT diverges"}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Vincent B767 (Eric 2026-02-17) — Slider seuil Jaccard */}
                      <div className="flex items-center gap-1.5" data-testid="fusion-preview-threshold-slider">
                        <label className="text-[10px] text-zinc-500 font-normal normal-case">
                          {lang === "fr" ? "Seuil" : "Threshold"}
                        </label>
                        <input
                          type="range"
                          min="0.20"
                          max="0.90"
                          step="0.05"
                          value={jaccardThreshold}
                          onChange={(e) => setJaccardThreshold(parseFloat(e.target.value))}
                          className="w-20 h-1 accent-teal-600"
                          data-testid="jaccard-threshold-input"
                          title={lang === "fr"
                            ? "Seuil Jaccard : plus bas = plus permissif (moins de divergences), plus haut = plus strict."
                            : "Jaccard threshold: lower = more permissive, higher = stricter."}
                        />
                        <span className="text-[10px] text-zinc-600 font-mono w-8" data-testid="jaccard-threshold-value">
                          {jaccardThreshold.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setPreviewPage((p) => Math.max(0, p - 1))}
                        disabled={currentPage === 0}
                        className="text-[10px] px-2 py-0.5 rounded border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="fusion-preview-prev"
                      >
                        ← {lang === "fr" ? "Précédent" : "Prev"}
                      </button>
                      <span className="text-[10px] text-zinc-500 px-1" data-testid="fusion-preview-page-indicator">
                        {currentPage + 1} / {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPreviewPage((p) => Math.min(totalPages - 1, p + 1))}
                        disabled={currentPage >= totalPages - 1}
                        className="text-[10px] px-2 py-0.5 rounded border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="fusion-preview-next"
                      >
                        {lang === "fr" ? "Suivant" : "Next"} →
                      </button>
                      </div>
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto space-y-2">
                    {rows.map((row, i) => (
                      <div key={i} className="grid grid-cols-3 gap-2 border-b border-zinc-100 pb-2 last:border-0" data-testid={`preview-row-${i}`}>
                        {/* Whisper column */}
                        <div
                          className={`text-[11px] leading-snug px-1 py-1 rounded border ${
                            row.wfTextDiff ? "bg-yellow-100 border-yellow-300" : "bg-blue-50/40 border-blue-100"
                          }`}
                          data-testid={`preview-whisper-seg-${i}`}
                          data-divergence-text={row.wfTextDiff ? "yes" : "no"}
                        >
                          {fusionSources.includes("whisper") && row.w ? (<>
                            <span className={`font-mono text-[10px] mr-1 ${row.wfSpeakerDiff ? "bg-yellow-200 text-yellow-900 px-1 rounded" : "text-blue-600"}`}>
                              {row.w.speaker || "?"}
                            </span>
                            {(row.w.text || "").slice(0, 100)}{(row.w.text || "").length > 100 ? "…" : ""}
                          </>) : (<span className="text-zinc-400 italic text-[10px]">—</span>)}
                        </div>
                        {/* Fireflies column (aligned) */}
                        <div
                          className={`text-[11px] leading-snug px-1 py-1 rounded border ${
                            row.wfTextDiff ? "bg-yellow-100 border-yellow-300" : "bg-orange-50/40 border-orange-100"
                          }`}
                          data-testid={`preview-fireflies-seg-${i}`}
                          data-divergence-text={row.wfTextDiff ? "yes" : "no"}
                          data-aligned-idx={row.fIdx}
                        >
                          {fusionSources.includes("fireflies") && row.f ? (<>
                            <span className={`font-mono text-[10px] mr-1 ${row.wfSpeakerDiff ? "bg-yellow-200 text-yellow-900 px-1 rounded" : "text-orange-600"}`}>
                              {row.f.speaker || "?"}
                            </span>
                            {(row.f.text || "").slice(0, 100)}{(row.f.text || "").length > 100 ? "…" : ""}
                          </>) : (<span className="text-zinc-400 italic text-[10px]">—</span>)}
                        </div>
                        {/* Merged column (B765) + bouton Corriger (B766) */}
                        <div
                          className={`text-[11px] leading-snug px-1 py-1 rounded border relative group ${
                            row.mDivFromBoth ? "bg-purple-100 border-purple-300" : "bg-teal-50/40 border-teal-100"
                          }`}
                          data-testid={`preview-merged-seg-${i}`}
                          data-divergence-merge={row.mDivFromBoth ? "yes" : "no"}
                        >
                          {row.m ? (<>
                            <span className={`font-mono text-[10px] mr-1 ${row.mSpeakerDiff ? "bg-purple-200 text-purple-900 px-1 rounded" : "text-teal-600"}`}>
                              {row.m.speaker || "?"}
                            </span>
                            {(row.m.merged_text || row.m.text || "").slice(0, 100)}{(row.m.merged_text || row.m.text || "").length > 100 ? "…" : ""}
                            {/* B766 : bouton inline corriger — visible si divergence GPT */}
                            {row.mDivFromBoth && (
                              <button
                                type="button"
                                onClick={() => {
                                  const segIdx = startIdx + i;
                                  setShowFusionSettings(false);
                                  if (typeof setTranscriptSubTab === "function") setTranscriptSubTab("merged");
                                  // Signale au MergedSubTab quel segment focuser
                                  try {
                                    window.dispatchEvent(new CustomEvent("knoria:merged-focus-segment", {
                                      detail: { videoId, segmentIndex: segIdx },
                                    }));
                                  } catch (_e) { /* silent */ }
                                  toast.info(
                                    lang === "fr"
                                      ? `Segment #${segIdx + 1} sélectionné dans l'éditeur Fusion.`
                                      : `Segment #${segIdx + 1} selected in Merged editor.`,
                                  );
                                }}
                                className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-600 text-white text-[9px] font-semibold hover:bg-purple-700 opacity-0 group-hover:opacity-100 transition-opacity absolute top-0.5 right-0.5"
                                data-testid={`preview-merged-fix-btn-${i}`}
                                title={lang === "fr" ? "Ouvrir ce segment dans l'éditeur de fusion" : "Open this segment in the Merged editor"}
                              >
                                ✎ {lang === "fr" ? "Corriger" : "Fix"}
                              </button>
                            )}
                          </>) : (<span className="text-zinc-400 italic text-[10px]">—</span>)}
                        </div>
                      </div>
                    ))}
                    {rows.length === 0 && (
                      <div className="text-[11px] text-zinc-500 italic text-center py-2">
                        {lang === "fr" ? "Aucun segment sur cette page" : "No segments on this page"}
                      </div>
                    )}
                  </div>
                  {/* Column headers */}
                  <div className="grid grid-cols-3 gap-2 mt-1 text-[10px] font-semibold text-zinc-500">
                    <div>🎙️ Whisper</div>
                    <div>🔥 Fireflies {bothShown ? "(aligné)" : ""}</div>
                    <div>🔀 {lang === "fr" ? "Fusion" : "Merged"}</div>
                  </div>
                </div>
              );
            })()}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowFusionSettings(false)}
                disabled={refusing}
                className="text-[11px] font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                data-testid="fusion-settings-cancel-btn"
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={handleFusionSettingsSubmit}
                disabled={refusing || fusionSources.length === 0}
                className="text-[11px] font-semibold px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="fusion-settings-submit-btn"
              >
                {refusing ? "⏳" : "🔀"} {lang === "fr" ? "Refaire la fusion" : "Re-run fusion"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
